import { NEWS_ARTICLES, localizeNewsArticle, newsArticlePath } from '@/features/actualidad-hostelera/articles'
import { LEARN_ARTICLES, learnArticlePath, localizeLearnArticle } from '@/features/aprender/articles'
import i18n from '@/i18n'
import { ROUTES } from '@/navigation/constants'
import type { LandingLocale, LocalizedSlugs } from '@/navigation/localeConstants'

export type AuthorId = 'samuel-carrillo' | 'javier-porras'

export type AuthorDefinition = {
    id: AuthorId
    slugs: LocalizedSlugs
    name: string
    role: string
    portraitSrc: string
    avatarSrc: string
    bioParagraphs: string[]
}

export type AuthorMeta = {
    id: AuthorId
    slug: string
    name: string
    role: string
    portraitSrc: string
    avatarSrc: string
    bioParagraphs: string[]
}

export type AuthorArticleLink = {
    id: string
    title: string
    description: string
    publishedAt: string
    section: 'aprender' | 'actualidad-hostelera'
    path: string
}

export const AUTHORS: AuthorDefinition[] = [
    {
        id: 'samuel-carrillo',
        slugs: { es: 'samuel-carrillo', en: 'samuel-carrillo' },
        name: 'Samuel Carrillo',
        role: 'Fundador de Tipi · Cocinero especializado en gestión',
        portraitSrc: '/authors/samuel-carrillo.webp',
        avatarSrc: '/authors/samuel-carrillo-avatar.webp',
        bioParagraphs: [
            'Samuel es un apasionado de la gastronomía que acabó estudiando cocina y dedicándose a ser cocinero porque se dio cuenta de que hacer que te paguen por comer es complicado. Se especializó en cocina tradicional y de producto, y dentro de los negocios, en gestión y rentabilidad.',
            'Su mayor pasión son las gambas rojas y, como guisos, cualquier guiso con salsa española. Lleva años gestionando restaurantes, tanto propios como ajenos, y ha trabajado de la mano de grandes cocineros como José Andrés y Juanjo López.',
            'Decidió crear Tipi al ver que una inmensa parte del sector estaba abandonada por las empresas que hacían tecnología para hostelería y los veían como los apestados: nadie quiere lidiar con un hostelero que vive en una bola de nieve.',
        ],
    },
    {
        id: 'javier-porras',
        slugs: { es: 'javier-porras', en: 'javier-porras' },
        name: 'Javier Porras',
        role: 'Hostelero · Gestión y rentabilidad',
        portraitSrc: '/authors/javier-porras.webp',
        avatarSrc: '/authors/javier-porras-avatar.webp',
        bioParagraphs: [
            'Javi lleva la hostelería en la sangre. Es la tercera generación de una familia de hosteleros y se ha criado entre fogones, barras, proveedores y servicios, conociendo desde joven la realidad del sector.',
            'Ha sido propietario y gestor de tres negocios de hostelería y ha llevado un cuarto desde el concepto hasta casi la apertura. Esa trayectoria le ha obligado a pelear el día a día: cartas rentables, márgenes, compras y conseguir que el negocio no solo facture, sino que deje beneficio.',
            'Formado en Gestión de Restaurantes y Food & Beverage Hotelero por Florida Universitària (Gastrouni), en Tipi aporta la visión de quien ha estado al otro lado. Su objetivo no es solo que los hosteleros entiendan mejor sus negocios, sino que recuperen el control sobre ellos.',
        ],
    },
]

export function authorI18nKey(id: AuthorId, field: string) {
    return `authors.${id}.${field}`
}

export function localizeAuthor(author: AuthorDefinition, locale: LandingLocale): AuthorMeta {
    const t = i18n.getFixedT(locale)
    return {
        id: author.id,
        slug: author.slugs[locale],
        name: String(t(authorI18nKey(author.id, 'name'), author.name)),
        role: String(t(authorI18nKey(author.id, 'role'), author.role)),
        portraitSrc: author.portraitSrc,
        avatarSrc: author.avatarSrc,
        bioParagraphs: author.bioParagraphs.map((fallback, index) =>
            String(t(authorI18nKey(author.id, `bio.${index + 1}`), fallback)),
        ),
    }
}

export function getAuthorById(id: string) {
    return AUTHORS.find(a => a.id === id)
}

export function getAuthorByName(name: string) {
    const normalized = name.trim().toLowerCase()
    return AUTHORS.find(a => a.name.toLowerCase() === normalized)
}

export function getAuthorByLocaleSlug(locale: LandingLocale, slug: string) {
    return AUTHORS.find(a => a.slugs[locale] === slug)
}

export function authorLocaleFreePath(author: AuthorDefinition) {
    return `${ROUTES.authors}/${author.slugs.es}`
}

export function authorPath(authorOrId: AuthorDefinition | AuthorId | string) {
    const author = typeof authorOrId === 'string' ? getAuthorById(authorOrId) : authorOrId
    if (!author) return ROUTES.authors
    return authorLocaleFreePath(author)
}

export function listAuthorArticles(authorName: string, locale: LandingLocale): AuthorArticleLink[] {
    const learn = LEARN_ARTICLES.filter(a => a.authorName === authorName).map(definition => {
        const article = localizeLearnArticle(definition, locale)
        return {
            id: article.id,
            title: article.title,
            description: article.description,
            publishedAt: article.publishedAt,
            section: 'aprender' as const,
            path: learnArticlePath(definition),
        }
    })

    const news = NEWS_ARTICLES.filter(a => a.authorName === authorName).map(definition => {
        const article = localizeNewsArticle(definition, locale)
        return {
            id: article.id,
            title: article.title,
            description: article.description,
            publishedAt: article.publishedAt,
            section: 'actualidad-hostelera' as const,
            path: newsArticlePath(definition),
        }
    })

    return [...learn, ...news].sort((a, b) => b.publishedAt.localeCompare(a.publishedAt))
}
